/**
 * Prepared Food Wastage (iter177 · Phase 1 · India).
 *
 * Simple daily register: search dish → qty → reason → save → refocus.
 * All financial calcs done backend-side and snapshotted per entry.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Loader2, Search, Settings, X, Beaker, Pencil, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet, FileText, Filter, ChevronDown, ChevronRight } from "lucide-react";

const SOURCE_STYLE = {
  RAW:  "bg-emerald-100 text-emerald-800 border-emerald-200",
  MENU: "bg-amber-100 text-amber-800 border-amber-200",
  PREP: "bg-sky-100 text-sky-800 border-sky-200",
};

const fmtINR = (v) => {
  const n = Number(v || 0);
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);


export function PreparedFoodWastageTab({ session, center, fmt }) {
  const isSA = !!session?.is_super_admin;
  const isAdmin = !!(session?.is_super_admin || session?.is_admin || session?.is_accounts);
  const canEdit = isAdmin || !!(session?.center);   // CM can enter for own centre

  const [date, setDate] = useState(todayISO());
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("Overproduction");
  const [remark, setRemark] = useState("");
  const [quantity, setQuantity] = useState("");
  // iter280 Phase 3 — optional actual purchase price for RAW/PREP items.
  const [actualPrice, setActualPrice] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ quantity: 0, food_cost_wastage: 0, potential_sales_lost: 0, entries: 0 });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pct, setPct] = useState(20);
  const searchRef = useRef(null);
  const qtyRef = useRef(null);

  const loadReasons = useCallback(async () => {
    try {
      const r = await api.post("/prepared-food-wastage/reasons/list",
                                  { token: session.token });
      setReasons(r.data.reasons || []);
    } catch (e) { /* silent */ }
  }, [session.token]);

  const loadPct = useCallback(async () => {
    try {
      const r = await api.post("/prepared-food-wastage/settings/get",
                                  { token: session.token });
      setPct(r.data.making_cost_percent);
    } catch (e) { /* silent */ }
  }, [session.token]);

  const loadEntries = useCallback(async () => {
    if (!center) return;
    try {
      const r = await api.post("/prepared-food-wastage/list", {
        token: session.token, center, date,
      });
      setEntries(r.data.entries || []);
      setTotals(r.data.totals || { quantity: 0, food_cost_wastage: 0, potential_sales_lost: 0, entries: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load wastage entries");
    }
  }, [session.token, center, date]);

  useEffect(() => { loadReasons(); loadPct(); }, [loadReasons, loadPct]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  // ── autocomplete debounce ──────────────────────────────────────
  useEffect(() => {
    if (!query || query.length < 2 || !center || center === "ALL") {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.post("/prepared-food-wastage/unified-search", {
          token: session.token, center, q: query, limit_per_source: 5,
        });
        setResults(r.data.items || []);
      } catch (e) {
        setResults([]);
      } finally { setSearching(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [query, center, session.token]);

  const pick = (it) => {
    setPicked(it);
    setQuery(it.name);
    setResults([]);
    setTimeout(() => qtyRef.current?.focus(), 10);
  };

  const resetEntry = () => {
    setPicked(null); setQuery(""); setQuantity("");
    setReason("Overproduction"); setRemark("");
    setActualPrice("");
    setResults([]);
    setTimeout(() => searchRef.current?.focus(), 20);
  };

  const submit = async () => {
    if (!center || center === "ALL") {
      toast.error("Please select a specific centre before recording wastage.");
      return;
    }
    if (!picked) {
      toast.error("Search and pick an item first.");
      return;
    }
    const q = Number(quantity);
    if (!q || q <= 0) {
      toast.error("Enter a valid quantity (>0)."); return;
    }
    if (!reason) { toast.error("Pick a reason."); return; }
    if (reason.toLowerCase() === "other" && !remark.trim()) {
      toast.error("Remark is required when reason = Other.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/prepared-food-wastage/create", {
        token: session.token, center, date,
        source: picked.source || "MENU",
        item_id: picked.item_id,
        menu_item_id: picked.item_id,   // legacy alias
        menu_item_name: picked.name,
        quantity: q, reason, remark: remark.trim(),
        // iter280 Phase 3 — optional actual purchase/effective price
        actual_price_per_unit: actualPrice ? Number(actualPrice) : null,
      });
      toast.success(`Recorded: ${picked.name} × ${q} ${picked.unit || ""}`);
      resetEntry();
      loadEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save wastage");
    } finally { setSaving(false); }
  };

  const voidEntry = async (row) => {
    const r = window.prompt(`Void reason for "${row.menu_item_name}" × ${row.quantity} (min 3 chars):`, "");
    if (!r || r.trim().length < 3) return;
    try {
      await api.post("/prepared-food-wastage/void", {
        token: session.token, entry_id: row.id, reason: r.trim(),
      });
      toast.success("Entry voided");
      loadEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to void");
    }
  };

  const savePct = async (newPct) => {
    try {
      await api.post("/prepared-food-wastage/settings/save", {
        token: session.token, making_cost_percent: Number(newPct),
      });
      toast.success(`Making Cost % updated to ${newPct}%. Historical entries preserved.`);
      setPct(Number(newPct));
      setSettingsOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update setting");
    }
  };

  const centerReadonly = !isAdmin;   // CM can never change centre
  const centerLabel = center || (isAdmin ? "— pick a centre —" : "—");
  const [tab, setTab] = useState("entry");

  return (
    <div className="space-y-4" data-testid="pfw-tab">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="pfw-tabs">
          <TabsTrigger value="entry" data-testid="pfw-tab-entry">Entry</TabsTrigger>
          <TabsTrigger value="register" data-testid="pfw-tab-register">Daily Register</TabsTrigger>
          <TabsTrigger value="preparation" data-testid="pfw-tab-preparation">Preparation</TabsTrigger>
          <TabsTrigger value="control" data-testid="pfw-tab-control">Wastage Control</TabsTrigger>
          {isSA && (
            <TabsTrigger value="prep" data-testid="pfw-tab-prep">
              <Beaker className="w-3.5 h-3.5 mr-1" />
              Prep Items
            </TabsTrigger>
          )}
        </TabsList>

      <TabsContent value="entry" className="mt-4 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Prepared Food Wastage</CardTitle>
            <CardDescription>
              One search across <strong>Raw Items</strong> (Item &amp; Price Master),
              {" "}<strong>Menu Dishes</strong> (POS Menu Master) and <strong>Prep Items</strong> (batter / mixes).
              Prices &amp; making cost are snapshotted per entry.
            </CardDescription>
          </div>
          {isSA && (
            <Button variant="outline" size="sm"
              onClick={() => setSettingsOpen(true)} data-testid="pfw-settings-btn">
              <Settings className="w-3.5 h-3.5 mr-1.5" />
              Making Cost {pct}%
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Date</Label>
              <Input type="date" value={date}
                onChange={(e) => setDate(e.target.value)} max={todayISO()}
                data-testid="pfw-date" />
            </div>
            <div className="space-y-1">
              <Label>Centre</Label>
              <Input value={centerLabel} readOnly={centerReadonly}
                disabled={centerReadonly}
                className={centerReadonly ? "bg-slate-100" : ""}
                data-testid="pfw-centre" />
              {centerReadonly && (
                <p className="text-[11px] text-muted-foreground">
                  Centre auto-pinned from your login.
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="pfw-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Autocomplete search */}
          <div className="grid grid-cols-1 md:grid-cols-[2fr_120px_2fr_140px] gap-3 items-end">
            <div className="space-y-1 relative">
              <Label>Item (Raw / Menu / Prep)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
                <Input ref={searchRef}
                  className="pl-8"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
                  placeholder='Type e.g. "tom", "akkha", "vada bat"…'
                  data-testid="pfw-menu-search"
                  disabled={!center || center === "ALL"}
                />
                {searching && <Loader2 className="w-4 h-4 absolute right-2 top-2.5 animate-spin" />}
                {results.length > 0 && !picked && (
                  <div className="absolute z-20 left-0 right-0 top-full mt-1 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
                    data-testid="pfw-menu-results">
                    {results.map((it) => (
                      <button key={`${it.source}-${it.item_id}`}
                        type="button"
                        onClick={() => pick(it)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                        data-testid={`pfw-menu-result-${it.name}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline"
                            className={`text-[10px] px-1.5 py-0 ${SOURCE_STYLE[it.source] || ""}`}>
                            {it.source}
                          </Badge>
                          <span className="font-medium truncate">{it.name}</span>
                          {it.category ? <span className="text-[11px] text-muted-foreground truncate">{it.category}</span> : null}
                        </span>
                        <span className="text-xs whitespace-nowrap">
                          {it.price_missing ? (
                            <span className="text-orange-700 font-semibold">PRICE MISSING</span>
                          ) : (
                            <span className="text-muted-foreground">
                              {fmtINR(it.cost_per_unit)} / {it.unit || ""}
                            </span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {picked && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${SOURCE_STYLE[picked.source] || ""}`}>
                    {picked.source}
                  </Badge>
                  {picked.price_missing ? (
                    <span className="text-orange-700 font-semibold">
                      PRICE MISSING — add a WAP in Item &amp; Price Master before recording wastage.
                    </span>
                  ) : picked.source === "MENU" ? (
                    <>Pre-GST ₹{picked.potential_sales_per_unit} · Making Cost {picked.making_cost_percent}% · ₹{picked.cost_per_unit} per {picked.unit || "MK"}</>
                  ) : (
                    <>₹{picked.cost_per_unit} per {picked.unit || ""}{picked.has_override ? " (centre override)" : ""}</>
                  )}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Qty {picked?.unit ? `(${picked.unit})` : ""}</Label>
              <Input ref={qtyRef} type="number" min="0" step="0.5"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="e.g. 2"
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                data-testid="pfw-quantity" />
            </div>
            <div className="space-y-1">
              <Label>Remark {reason.toLowerCase() === "other" && <span className="text-red-500">*</span>}</Label>
              <Input value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Optional (required if reason = Other)"
                data-testid="pfw-remark" />
            </div>
            {picked && (picked.source === "RAW" || picked.source === "PREP") && (
              <div className="space-y-1">
                <Label className="text-xs">Actual ₹ / {picked.unit || "unit"} <span className="text-slate-400">(optional)</span></Label>
                <Input type="number" step="0.01" min="0"
                  value={actualPrice}
                  onChange={(e) => setActualPrice(e.target.value)}
                  placeholder={`Master: ₹${picked.cost_per_unit ?? ""}`}
                  data-testid="pfw-actual-price"
                  title="Enter today's actual market price if it differs from the master. Historical rows stay untouched." />
              </div>
            )}
            <div>
              <Button onClick={submit} disabled={saving || !picked || !quantity || picked?.price_missing}
                className="w-full" data-testid="pfw-add-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Wastage
              </Button>
            </div>
          </div>

          {picked && (
            <div className="rounded-md border border-purple-200 bg-purple-50 p-3 text-sm">
              <Badge variant="outline"
                className={`mr-2 text-[10px] px-1.5 py-0 ${SOURCE_STYLE[picked.source] || ""}`}>
                {picked.source}
              </Badge>
              <span className="font-semibold">{picked.name}</span>
              {quantity && Number(quantity) > 0 && (
                <span className="ml-3">
                  · Food Cost Wastage <strong className="text-purple-800">{fmtINR(Number(quantity) * picked.cost_per_unit)}</strong>
                  {picked.source === "MENU" && picked.potential_sales_per_unit && (
                    <> · Potential Sales Lost <strong>{fmtINR(Number(quantity) * picked.potential_sales_per_unit)}</strong></>
                  )}
                </span>
              )}
              <Button variant="ghost" size="sm" className="ml-2 h-6 px-2"
                onClick={() => { setPicked(null); setQuery(""); }}
                data-testid="pfw-clear-pick">
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* iter177f — Selected-date wastage list visible on the Entry tab
          so the CM can confirm what they just added. Full history stays
          on the Daily Register tab. */}
      <Card data-testid="pfw-entry-today-list">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Wastage for {date} {center ? `· ${center}` : ""}
          </CardTitle>
          <CardDescription>
            {entries.length} entr{entries.length === 1 ? "y" : "ies"} ·
            Food Cost <strong>{fmtINR(totals.food_cost_wastage)}</strong>
            {totals.potential_sales_lost > 0 && (
              <> · Potential Sales Lost <strong>{fmtINR(totals.potential_sales_lost)}</strong></>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Wastage ₹</TableHead>
                  <TableHead className="text-right">Pot. Sales ₹</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Remark</TableHead>
                  <TableHead>Entered By</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-4"
                      data-testid="pfw-entry-empty">
                      No wastage recorded yet for {date}. Add one above.
                    </TableCell>
                  </TableRow>
                )}
                {entries.map((r, i) => {
                  // Treat old rows without a ``source`` as MENU (Q2 = A).
                  const src = r.source || "MENU";
                  return (
                    <TableRow key={r.id || i} data-testid={`pfw-entry-row-${i}`}>
                      <TableCell className="text-xs">{r.created_at?.slice(11, 16)}</TableCell>
                      <TableCell className="font-medium">
                        {r.menu_item_name}
                        {r.unit ? <span className="text-[10px] text-muted-foreground ml-1">/ {r.unit}</span> : null}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline"
                          className={`text-[10px] px-1.5 py-0 ${SOURCE_STYLE[src] || ""}`}>
                          {src}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{r.quantity}</TableCell>
                      <TableCell className="text-right font-semibold text-purple-700">
                        {fmtINR(r.food_cost_wastage)}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.potential_sales_value_lost != null
                          ? fmtINR(r.potential_sales_value_lost)
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">{r.reason}</TableCell>
                      <TableCell className="text-xs">{r.remark || "—"}</TableCell>
                      <TableCell className="text-xs">{r.created_by}</TableCell>
                      <TableCell>
                        {r.voided ? (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-red-100 text-red-800">Voided</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-800">Saved</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="register" className="mt-4 space-y-4">
      {/* Daily totals + register */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily Register — {date}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Portions Wasted" value={`${totals.quantity} MK`} testId="pfw-tile-qty" />
            <StatCard label="Food Cost Wastage" value={fmtINR(totals.food_cost_wastage)} testId="pfw-tile-food-cost" accent="purple" />
            <StatCard label="Potential Sales Lost" value={fmtINR(totals.potential_sales_lost)} testId="pfw-tile-sales-lost" />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Cost / Unit</TableHead>
                <TableHead className="text-right">Food Cost ₹</TableHead>
                <TableHead className="text-right">Pot. Sales ₹</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Remark</TableHead>
                <TableHead>Entered By</TableHead>
                {isAdmin && <TableHead>Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 && (
                <TableRow><TableCell colSpan={isAdmin ? 11 : 10} className="text-center text-muted-foreground py-6">
                  No wastage recorded for {date}.
                </TableCell></TableRow>
              )}
              {entries.map((r, i) => {
                const src = r.source || "MENU";
                const costPerUnit = r.cost_per_unit_snapshot
                  ?? r.making_cost_per_mk ?? 0;
                return (
                <TableRow key={r.id} data-testid={`pfw-row-${i}`}>
                  <TableCell className="text-xs">{r.created_at?.slice(11, 16)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${SOURCE_STYLE[src] || ""}`}>
                      {src}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">
                    {r.menu_item_name}
                    {r.category && <span className="ml-2 text-[10px] text-muted-foreground">{r.category}</span>}
                  </TableCell>
                  <TableCell className="text-right">{r.quantity} {r.unit || ""}</TableCell>
                  <TableCell className="text-right">{fmtINR(costPerUnit)}</TableCell>
                  <TableCell className="text-right font-semibold text-purple-700">{fmtINR(r.food_cost_wastage)}</TableCell>
                  <TableCell className="text-right">
                    {r.potential_sales_value_lost != null
                      ? fmtINR(r.potential_sales_value_lost)
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><Badge variant="outline">{r.reason}</Badge></TableCell>
                  <TableCell className="text-xs">{r.remark}</TableCell>
                  <TableCell className="text-xs">{r.created_by}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Button variant="ghost" size="sm"
                        onClick={() => voidEntry(r)}
                        data-testid={`pfw-void-${i}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-600" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="preparation" className="mt-4">
        <PreparationTab session={session} center={center} date={date}
          setDate={setDate} isAdmin={isAdmin} isSA={isSA} />
      </TabsContent>

      {isSA && (
        <TabsContent value="prep" className="mt-4">
          <PrepItemsTab session={session} />
        </TabsContent>
      )}

      <TabsContent value="control" className="mt-4">
        <WastageControlTab session={session} center={center}
          isAdmin={isAdmin} isSA={isSA} />
      </TabsContent>
      </Tabs>

      {settingsOpen && (
        <SettingsModal pct={pct} onSave={savePct} onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}


function StatCard({ label, value, accent, testId }) {
  const cls = accent === "purple"
    ? "bg-purple-50 border-purple-200 text-purple-800"
    : "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`p-3 rounded-lg border ${cls}`} data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-lg font-semibold mt-1">{value}</p>
    </div>
  );
}


function SettingsModal({ pct, onSave, onClose }) {
  const [v, setV] = useState(pct);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" data-testid="pfw-settings-modal">
        <h3 className="text-lg font-semibold mb-2">Prepared Food Making Cost %</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Applies to <strong>new</strong> wastage entries only.  Historical entries snapshot the % used at time of entry and are never recomputed.
        </p>
        <Input type="number" min="0" max="100" step="0.5"
          value={v} onChange={(e) => setV(e.target.value)}
          data-testid="pfw-settings-value" />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(v)} data-testid="pfw-settings-save">Save</Button>
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// iter279 · Phase 2 — Food Preparation register (Prep/Waste/Remaining)
// ─────────────────────────────────────────────────────────────
function PreparationTab({ session, center, date, setDate, isAdmin, isSA }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [unit, setUnit] = useState("");
  const [preparedQty, setPreparedQty] = useState("");
  const [preparedBy, setPreparedBy] = useState("");
  const [batchTime, setBatchTime] = useState("");
  const [remarks, setRemarks] = useState("");
  // iter280 · Phase 3 — optional actual/effective purchase price paid.
  const [defaultPrice, setDefaultPrice] = useState("");
  const [actualPricePerUnit, setActualPricePerUnit] = useState("");
  const [batches, setBatches] = useState([]);
  const [summary, setSummary] = useState([]);
  const [saving, setSaving] = useState(false);

  const scoped = center && center !== "ALL";

  const loadDay = useCallback(async () => {
    if (!scoped || !date) { setBatches([]); setSummary([]); return; }
    try {
      const [b, s] = await Promise.all([
        api.post("/food-preparation/list",
          { token: session.token, center, from_date: date, to_date: date }),
        api.post("/food-preparation/daily-summary",
          { token: session.token, center, date }),
      ]);
      setBatches(b.data.rows || []);
      setSummary(s.data.rows || []);
    } catch (e) { /* silent */ }
  }, [session.token, center, date, scoped]);

  useEffect(() => { loadDay(); }, [loadDay]);

  useEffect(() => {
    if (!query || query.length < 2 || !scoped) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/prepared-food-wastage/unified-search",
          { token: session.token, center, q: query, limit_per_source: 5 });
        setResults((r.data.items || []).filter(
          (it) => (it.source || "").toUpperCase() !== "RAW"));
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [query, center, scoped, session.token]);

  const pickItem = (it) => {
    setPicked(it); setQuery(it.name); setResults([]);
    setUnit((it.unit || "").toUpperCase() || "PCS");
  };

  const save = async () => {
    if (!picked || !unit || !preparedQty) {
      toast.error("Pick item, unit and prepared quantity."); return;
    }
    if (Number(preparedQty) <= 0) {
      toast.error("Prepared quantity must be > 0."); return;
    }
    setSaving(true);
    try {
      await api.post("/food-preparation/create", {
        token: session.token, center, date,
        source: (picked.source || "MENU").toUpperCase(),
        item_id: picked.id || picked.code,
        item_code: picked.code || "",
        item_name: picked.name,
        category: picked.category || "",
        unit: unit.toUpperCase(),
        prepared_qty: Number(preparedQty),
        prepared_by: preparedBy, batch_time: batchTime, remarks,
        // iter280 · Phase 3 — actual/effective price snapshot.
        default_price_per_unit: defaultPrice ? Number(defaultPrice) : null,
        actual_price_per_unit: actualPricePerUnit ? Number(actualPricePerUnit) : null,
      });
      toast.success(`Batch saved: ${picked.name} · ${preparedQty} ${unit}`);
      setPicked(null); setQuery(""); setPreparedQty("");
      setPreparedBy(""); setBatchTime(""); setRemarks("");
      setDefaultPrice(""); setActualPricePerUnit("");
      loadDay();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const voidBatch = async (id) => {
    if (!window.confirm("Void this preparation batch? Wastage rows against it must be voided first.")) return;
    try {
      await api.post("/food-preparation/void",
        { token: session.token, id });
      toast.success("Batch voided");
      loadDay();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
  };

  if (!scoped) {
    return <Card><CardContent className="py-8 text-center text-sm text-slate-500">
      Select a specific centre to record preparation batches.
    </CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Record Preparation Batch</CardTitle>
          <CardDescription>
            Prepared quantity is the upper bound for wastage on the same
            date. Wastage entries above the remaining qty are rejected.
            Different units cannot be reconciled in this phase.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-600">Date</label>
              <Input type="date" value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="fp-date" />
            </div>
            <div className="md:col-span-3 relative">
              <label className="text-xs text-slate-600">Prepared Item</label>
              <Input value={query} placeholder="Search menu / prep items…"
                onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
                data-testid="fp-search" />
              {results.length > 0 && !picked && (
                <div className="absolute z-30 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-72 overflow-auto">
                  {results.map((it) => (
                    <div key={`${it.source}-${it.id || it.code}`}
                      className="p-2 hover:bg-slate-50 cursor-pointer flex justify-between text-sm border-b last:border-none"
                      onClick={() => pickItem(it)}
                      data-testid={`fp-pick-${it.id || it.code}`}>
                      <div>
                        <div className="font-medium">{it.name}</div>
                        <div className="text-xs text-slate-500">
                          {it.source} · {it.unit || "—"} · {it.category || ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs text-slate-600">Unit</label>
              <Input value={unit} onChange={(e) => setUnit(e.target.value)}
                placeholder="KG / PLATE / PCS…"
                data-testid="fp-unit" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Prepared Qty</label>
              <Input type="number" step="0.001" value={preparedQty}
                onChange={(e) => setPreparedQty(e.target.value)}
                data-testid="fp-qty" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Prepared By</label>
              <Input value={preparedBy}
                onChange={(e) => setPreparedBy(e.target.value)}
                placeholder="Chef name" data-testid="fp-by" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Batch Time</label>
              <Input value={batchTime}
                onChange={(e) => setBatchTime(e.target.value)}
                placeholder="10:30" data-testid="fp-time" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Remarks</label>
              <Input value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional" data-testid="fp-remarks" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-600">
                Default ₹ / {unit || "unit"} <span className="text-slate-400">(from master)</span>
              </label>
              <Input type="number" step="0.0001" min="0"
                value={defaultPrice}
                onChange={(e) => setDefaultPrice(e.target.value)}
                placeholder="e.g. 30.00" data-testid="fp-default-price" />
            </div>
            <div>
              <label className="text-xs text-slate-600">
                Actual ₹ / {unit || "unit"} <span className="text-slate-400">(if paid differently today)</span>
              </label>
              <Input type="number" step="0.0001" min="0"
                value={actualPricePerUnit}
                onChange={(e) => setActualPricePerUnit(e.target.value)}
                placeholder="e.g. 36.50 or 0.60"
                data-testid="fp-actual-price"
                title="Actual price for this batch only. Item Master is never overwritten." />
            </div>
            <div className="md:col-span-2 text-xs text-slate-500 self-end">
              {actualPricePerUnit
                ? <>Effective ₹ used for this batch: <strong className="text-emerald-700">₹{Number(actualPricePerUnit).toFixed(2)}</strong> (actual override)</>
                : defaultPrice
                  ? <>Effective ₹ used for this batch: <strong>₹{Number(defaultPrice).toFixed(2)}</strong> (master default)</>
                  : <em>Leave both blank if you do not want to attach a per-unit price to this batch.</em>}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button onClick={save} disabled={saving || !picked}
              data-testid="fp-save">
              {saving ? "Saving…" : "Save Preparation Batch"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">
              Today's Preparation vs Wastage ({date})
            </CardTitle>
            <CardDescription>
              Prepared / Wasted / Remaining reconciliation (same-unit).
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={loadDay}
            data-testid="fp-refresh">Refresh</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-2 py-1.5">Item</th>
                <th className="px-2 py-1.5">Unit</th>
                <th className="px-2 py-1.5 text-right">Prepared</th>
                <th className="px-2 py-1.5 text-right">Wasted</th>
                <th className="px-2 py-1.5 text-right">Remaining</th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 && (
                <tr><td colSpan={5} className="px-2 py-6 text-center text-slate-400 italic">No preparation batches yet for {date}.</td></tr>
              )}
              {summary.map((r) => (
                <tr key={`${r.item_id}-${r.unit}`}
                  className="border-t border-slate-100"
                  data-testid={`fp-sum-${r.item_id}`}>
                  <td className="px-2 py-1.5 font-medium">{r.item_name}</td>
                  <td className="px-2 py-1.5">{r.unit}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.prepared_qty}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-amber-700">{r.wasted_qty}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold text-emerald-700">{r.remaining_qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Batches on {date}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-2 py-1.5">Time</th>
                <th className="px-2 py-1.5">Item</th>
                <th className="px-2 py-1.5">Unit</th>
                <th className="px-2 py-1.5 text-right">Prepared</th>
                <th className="px-2 py-1.5">By</th>
                <th className="px-2 py-1.5">Remarks</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 && (
                <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400 italic">—</td></tr>
              )}
              {batches.map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 font-mono">{b.batch_time || "—"}</td>
                  <td className="px-2 py-1.5 font-medium">{b.item_name}</td>
                  <td className="px-2 py-1.5">{b.unit}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{b.prepared_qty}</td>
                  <td className="px-2 py-1.5">{b.prepared_by || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-500">{b.remarks || ""}</td>
                  <td className="px-2 py-1.5 text-right">
                    {(isAdmin || isSA) && (
                      <Button size="sm" variant="ghost" onClick={() => voidBatch(b.id)}
                        data-testid={`fp-void-${b.id}`}>Void</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}


// ── Prep Items Master (SA-only) ─────────────────────────────────
function PrepItemsTab({ session }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // item or {} for new

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post("/prepared-food-wastage/prep-items/list",
                                     { token: session.token });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load prep items");
    } finally { setLoading(false); }
  }, [session.token]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Prep Item Master</CardTitle>
          <CardDescription>
            Intermediate preparations that live in neither the Item &amp; Price
            Master nor the POS Menu Master. Global by default; add a centre
            override only when a specific centre has a different batch cost.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => setEditing({})}
          data-testid="pfw-prep-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New Prep Item
        </Button>
      </CardHeader>
      <CardContent>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Batch Cost ₹</TableHead>
              <TableHead className="text-right">Batch Yield</TableHead>
              <TableHead className="text-right">Cost / Unit ₹</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={7}
                className="text-center text-muted-foreground py-6">
                No prep items yet.  Click &quot;New Prep Item&quot; to add Vada Batter,
                Starter Batter, Prepared Mixes, etc.
              </TableCell></TableRow>
            )}
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`pfw-prep-row-${it.name}`}>
                <TableCell className="font-medium">{it.name}</TableCell>
                <TableCell>{it.unit}</TableCell>
                <TableCell className="text-right">{Number(it.batch_cost || 0).toFixed(2)}</TableCell>
                <TableCell className="text-right">{Number(it.batch_yield || 0).toFixed(2)} {it.unit}</TableCell>
                <TableCell className="text-right font-semibold">
                  ₹{Number(it.cost_per_unit || 0).toFixed(2)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    it.active ? "bg-emerald-50 text-emerald-800"
                              : "bg-slate-100 text-slate-600"}>
                    {it.active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm"
                    onClick={() => setEditing(it)}
                    data-testid={`pfw-prep-edit-${it.name}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      {editing && (
        <PrepItemModal item={editing} session={session}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </Card>
  );
}


function PrepItemModal({ item, session, onClose, onSaved }) {
  const isEdit = !!item?.id;
  const [name, setName] = useState(item?.name || "");
  const [unit, setUnit] = useState(item?.unit || "kg");
  const [batchCost, setBatchCost] = useState(item?.batch_cost ?? "");
  const [batchYield, setBatchYield] = useState(item?.batch_yield ?? "");
  const [active, setActive] = useState(item?.active !== false);
  const [busy, setBusy] = useState(false);
  const costPerUnit = useMemo(() => {
    const c = Number(batchCost), y = Number(batchYield);
    if (!c || !y) return 0;
    return c / y;
  }, [batchCost, batchYield]);
  const save = async () => {
    if (!name.trim() || !unit.trim()) {
      toast.error("Name and unit are required."); return;
    }
    if (!(Number(batchCost) > 0) || !(Number(batchYield) > 0)) {
      toast.error("Batch cost and batch yield must be > 0."); return;
    }
    setBusy(true);
    try {
      await api.post("/prepared-food-wastage/prep-items/save", {
        token: session.token,
        id: item?.id,
        name: name.trim(), unit: unit.trim(),
        batch_cost: Number(batchCost),
        batch_yield: Number(batchYield),
        active,
      });
      toast.success(`Prep item ${isEdit ? "updated" : "saved"}.`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3"
        data-testid="pfw-prep-modal">
        <h3 className="text-lg font-semibold">
          {isEdit ? "Edit Prep Item" : "New Prep Item"}
        </h3>
        <div className="space-y-1">
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Vada Batter"
            data-testid="pfw-prep-name" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label>Unit</Label>
            <Input value={unit} onChange={(e) => setUnit(e.target.value)}
              placeholder="kg / litre / piece"
              data-testid="pfw-prep-unit" />
          </div>
          <div className="space-y-1">
            <Label>Active</Label>
            <div className="flex items-center h-9">
              <Switch checked={active} onCheckedChange={setActive}
                data-testid="pfw-prep-active" />
              <span className="ml-2 text-xs">{active ? "Active" : "Inactive"}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label>Batch Cost ₹</Label>
            <Input type="number" min="0" step="0.5" value={batchCost}
              onChange={(e) => setBatchCost(e.target.value)}
              data-testid="pfw-prep-batch-cost" />
          </div>
          <div className="space-y-1">
            <Label>Batch Yield ({unit || "unit"})</Label>
            <Input type="number" min="0" step="0.1" value={batchYield}
              onChange={(e) => setBatchYield(e.target.value)}
              data-testid="pfw-prep-batch-yield" />
          </div>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-sm">
          Cost per {unit || "unit"} = <strong>₹{costPerUnit.toFixed(2)}</strong>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Do NOT add Milk / Tomato / Palak here — those belong in the Item &amp; Price Master.
          Do NOT add menu dishes here — those live in POS Menu Master.
        </p>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}
            data-testid="pfw-prep-save">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}


export default PreparedFoodWastageTab;


// ══════════════════════════════════════════════════════════════════════
// iter177d — Wastage Control Dashboard (4th tab)
// ══════════════════════════════════════════════════════════════════════
const STATUS_STYLE = {
  GREEN: "bg-emerald-100 text-emerald-800 border-emerald-300",
  AMBER: "bg-amber-100 text-amber-900 border-amber-300",
  RED:    "bg-red-100 text-red-800 border-red-300",
  NOT_SET: "bg-slate-100 text-slate-600 border-slate-300",
};
const STATUS_LABEL = {
  GREEN: "Within Benchmark",
  AMBER: "Near Benchmark",
  RED: "Above Benchmark",
  NOT_SET: "Benchmark Not Set",
};

function firstOfMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function todayISO2() { return new Date().toISOString().slice(0, 10); }

function WastageControlTab({ session, center, isAdmin, isSA }) {
  const [fromDate, setFromDate] = useState(firstOfMonthISO());
  const [toDate, setToDate] = useState(todayISO2());
  const [scope, setScope] = useState(isAdmin ? (center || "ALL") : "own");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingBenchmark, setEditingBenchmark] = useState(false);
  const [centersList, setCentersList] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = { token: session.token, from_date: fromDate, to_date: toDate };
      if (isAdmin) body.center = scope;   // may be "ALL" or a specific code
      const r = await api.post("/prepared-food-wastage/control-dashboard", body);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
      setData(null);
    } finally { setLoading(false); }
  }, [session.token, fromDate, toDate, scope, isAdmin]);
  useEffect(() => { load(); }, [load]);

  // Load list of centres once for SA scope selector.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const r = await api.get("/centers");
        setCentersList((r.data?.centers || []).filter((c) => c.active !== false));
      } catch { /* silent */ }
    })();
  }, [isAdmin]);

  const downloadExcel = async () => {
    if (scope === "ALL") { toast.error("Pick a specific centre to export."); return; }
    try {
      const r = await api.post("/prepared-food-wastage/control-dashboard/excel",
        { token: session.token, center: scope, from_date: fromDate, to_date: toDate },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `wastage_control_${scope}_${fromDate}_${toDate}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Excel failed"); }
  };
  const downloadPdf = async () => {
    if (scope === "ALL") { toast.error("Pick a specific centre to export."); return; }
    try {
      const r = await api.post("/prepared-food-wastage/control-dashboard/pdf",
        { token: session.token, center: scope, from_date: fromDate, to_date: toDate },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `wastage_control_${scope}_${fromDate}_${toDate}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF failed"); }
  };

  const mode = data?.mode;
  const d1 = data?.dashboard;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="space-y-1">
              <Label>From</Label>
              <Input type="date" value={fromDate} max={todayISO2()}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="wc-from" />
            </div>
            <div className="space-y-1">
              <Label>To</Label>
              <Input type="date" value={toDate} max={todayISO2()}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="wc-to" />
            </div>
            {isAdmin && (
              <div className="space-y-1 min-w-[220px]">
                <Label>Centre</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger data-testid="wc-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">ALL CENTRES (comparison)</SelectItem>
                    {centersList.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadExcel}
                disabled={scope === "ALL"} data-testid="wc-excel">
                Excel
              </Button>
              <Button variant="outline" size="sm" onClick={downloadPdf}
                disabled={scope === "ALL"} data-testid="wc-pdf">
                PDF
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Operational KPI: Wastage % of Food Cost (benchmarked). Business view: Wastage % of Sales (separate).
            <strong className="ml-1">No automatic salary deduction or FO charge.</strong> This dashboard measures — it does not settle.
          </p>
        </CardHeader>
      </Card>

      {loading && <Loader2 className="w-5 h-5 animate-spin mx-auto" />}

      {!loading && mode === "ALL" && (
        <AllCentresView data={data} isSA={isSA}
          onEditBenchmark={(c) => { setScope(c); setEditingBenchmark(true); }} />
      )}

      {!loading && mode === "ONE" && d1 && (
        <SingleCentreView dash={d1} isSA={isSA}
          session={session} center={scope}
          fromDate={fromDate} toDate={toDate}
          onEditBenchmark={() => setEditingBenchmark(true)} />
      )}

      {editingBenchmark && isSA && scope !== "ALL" && (
        <BenchmarkModal center={scope} session={session}
          initial={d1 || {}}
          onClose={() => setEditingBenchmark(false)}
          onSaved={() => { setEditingBenchmark(false); load(); }} />
      )}
    </div>
  );
}


function KPI({ label, value, sub, accent, testId }) {
  const bg = accent === "purple" ? "bg-purple-50 border-purple-200 text-purple-800"
           : accent === "green"  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
           : accent === "red"    ? "bg-red-50 border-red-200 text-red-800"
           : accent === "amber"  ? "bg-amber-50 border-amber-200 text-amber-900"
           : "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`p-3 rounded-lg border ${bg}`} data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-xl font-semibold mt-1">{value}</p>
      {sub && <p className="text-[11px] opacity-70 mt-1">{sub}</p>}
    </div>
  );
}


// iter183 — collapsible section wrapper for the Wastage Control tab.
function WastageSection({ open, onToggle, testId, title, hint, extra, children }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <button type="button"
            onClick={onToggle}
            className="flex items-center gap-2 text-left flex-1 group"
            data-testid={`${testId}-toggle`}
            aria-expanded={open}>
            {open ? <ChevronDown className="w-4 h-4 text-slate-500 group-hover:text-purple-600" />
                  : <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-purple-600" />}
            <CardTitle className="text-base group-hover:text-purple-700 transition-colors">{title}</CardTitle>
            {hint && <span className="ml-2 text-[11px] font-normal text-slate-400">{hint}</span>}
          </button>
          {extra}
        </div>
      </CardHeader>
      {open && <CardContent>{children}</CardContent>}
    </Card>
  );
}

function SingleCentreView({ dash, isSA, onEditBenchmark, session, center,
                                 fromDate, toDate }) {
  const fmt = (v) => v == null ? "—"
    : `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const status = dash.status;

  // iter182 — drill-down filter from clicking a reason row.
  const [drillReason, setDrillReason] = useState(null);

  // iter183 — per-section expand/collapse state (UI only).
  const [openSections, setOpenSections] = useState({
    summary: true,   // KPI Tiles      — open by default
    source:  false,  // Wastage by Source
    reason:  true,   // Wastage by Reason — open by default
    top:     true,   // Top Wasted Items — open by default
    daily:   false,  // Date-wise Summary
    detail:  false,  // Detailed Wastage Report
  });
  const toggleSection = (key) => setOpenSections(p => ({ ...p, [key]: !p[key] }));

  return (
    <div className="space-y-4">
      {/* KPI Tiles — collapsible */}
      <WastageSection open={openSections.summary} onToggle={() => toggleSection("summary")} testId="wc-section-summary"  title="Summary" hint="KPI tiles">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <KPI label="Actual Wastage ₹"
            value={fmt(dash.actual_wastage)}
            sub={`${dash.entries_count} entries`}
            accent="purple" testId="wc-tile-actual" />
          <KPI label="Potential Sales Lost ₹"
            value={fmt(dash.potential_sales_lost)}
            sub="MENU × centre POS price"
            accent="amber" testId="wc-tile-potential" />
          <KPI label="Wastage % of Food Cost"
            value={`${Number(dash.wastage_pct_food_cost).toFixed(2)}%`}
            sub={`Food Cost ${fmt(dash.food_cost)}`}
            accent="slate" testId="wc-tile-pct-food" />
          <KPI label="Benchmark %"
            value={dash.wastage_benchmark_pct == null
              ? "NOT SET" : `${Number(dash.wastage_benchmark_pct).toFixed(2)}%`}
            sub={dash.wastage_amber_threshold_pct != null
              ? `Amber @ ${Number(dash.wastage_amber_threshold_pct).toFixed(2)}%` : "Amber threshold not set"}
            accent={dash.wastage_benchmark_pct == null ? "slate" : "amber"}
            testId="wc-tile-benchmark" />
          <KPI label="Excess Wastage ₹"
            value={dash.excess_wastage == null ? "—" : fmt(dash.excess_wastage)}
            sub={dash.allowed_wastage != null
              ? `Allowed ${fmt(dash.allowed_wastage)}`
              : "Set a benchmark to compute"}
            accent={status === "RED" ? "red"
                   : status === "AMBER" ? "amber"
                   : status === "GREEN" ? "green" : "slate"}
            testId="wc-tile-excess" />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-3">
          <Badge variant="outline"
            className={`text-sm px-3 py-1 ${STATUS_STYLE[status] || ""}`}
            data-testid="wc-status-badge">
            {STATUS_LABEL[status] || status}
          </Badge>
          <span className="text-sm text-muted-foreground">
            Wastage % of Sales:{" "}
            <strong>{Number(dash.wastage_pct_sales || 0).toFixed(4)}%</strong>
            {" "}({fmt(dash.period_sales)} sales)
          </span>
          {isSA && (
            <Button variant="outline" size="sm" onClick={onEditBenchmark}
              data-testid="wc-edit-benchmark-btn" className="ml-auto">
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              {dash.wastage_benchmark_pct == null ? "Set Benchmark" : "Edit Benchmark"}
            </Button>
          )}
        </div>
      </WastageSection>

      {/* Wastage by Source — collapsed by default */}
      <WastageSection open={openSections.source} onToggle={() => toggleSection("source")} testId="wc-section-source"  title="Wastage by Source">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">₹</TableHead>
              <TableHead className="text-right">Entries</TableHead>
              <TableHead className="text-right">Pot. Sales ₹</TableHead>
              <TableHead className="text-right">% Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(dash.by_source || []).map((r) => (
              <TableRow key={r.source}>
                <TableCell>
                  <Badge variant="outline"
                    className={`text-[10px] px-1.5 py-0 ${SOURCE_STYLE[r.source] || ""}`}>
                    {r.source}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-semibold text-purple-700">{fmt(r.food_cost_wastage)}</TableCell>
                <TableCell className="text-right">{r.entries}</TableCell>
                <TableCell className="text-right font-medium text-amber-700">
                  {r.potential_sales_lost == null ? "—" : fmt(r.potential_sales_lost)}
                </TableCell>
                <TableCell className="text-right">{r.pct_of_total}%</TableCell>
              </TableRow>
            ))}
            {(dash.by_source || []).length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">No wastage in this period.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </WastageSection>

      {/* Wastage by Reason — open by default */}
      <WastageSection open={openSections.reason} onToggle={() => toggleSection("reason")} testId="wc-section-reason"  title="Wastage by Reason" hint="click a row to drill down">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">₹</TableHead>
              <TableHead className="text-right">Entries</TableHead>
              <TableHead className="text-right">Pot. Sales ₹</TableHead>
              <TableHead className="text-right">% Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(dash.by_reason || []).map((r) => (
              <TableRow key={r.reason}
                className={`cursor-pointer transition-colors ${drillReason === r.reason ? "bg-purple-50" : "hover:bg-slate-50"}`}
                onClick={() => {
                  setDrillReason(prev => prev === r.reason ? null : r.reason);
                  setTimeout(() => document.querySelector('[data-testid="wc-detail-report"]')
                                          ?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
                }}
                data-testid={`wc-reason-row-${r.reason.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                <TableCell className="font-medium">
                  {r.reason}
                  {drillReason === r.reason && (
                    <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0 bg-purple-100 text-purple-700 border-purple-200">drilled</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-semibold text-purple-700">{fmt(r.food_cost_wastage)}</TableCell>
                <TableCell className="text-right">{r.entries}</TableCell>
                <TableCell className="text-right font-medium text-amber-700">
                  {r.potential_sales_lost ? fmt(r.potential_sales_lost) : "—"}
                </TableCell>
                <TableCell className="text-right">{r.pct_of_total}%</TableCell>
              </TableRow>
            ))}
            {(dash.by_reason || []).length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">—</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </WastageSection>

      {/* Top Wasted Items — open by default */}
      <WastageSection open={openSections.top} onToggle={() => toggleSection("top")} testId="wc-section-top"  title="Top Wasted Items">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Wastage ₹</TableHead>
              <TableHead className="text-right">Pot. Sales ₹</TableHead>
              <TableHead className="text-right">% Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(dash.top_items || []).map((r, i) => (
              <TableRow key={r.item}>
                <TableCell className="text-slate-500">{i + 1}</TableCell>
                <TableCell className="font-medium">{r.item}</TableCell>
                <TableCell>
                  <Badge variant="outline"
                    className={`text-[10px] px-1.5 py-0 ${SOURCE_STYLE[r.source] || ""}`}>
                    {r.source}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">{r.quantity} {r.unit}</TableCell>
                <TableCell className="text-right font-semibold text-purple-700">{fmt(r.food_cost_wastage)}</TableCell>
                <TableCell className="text-right font-medium text-amber-700">
                  {r.potential_sales_lost == null ? "—" : fmt(r.potential_sales_lost)}
                </TableCell>
                <TableCell className="text-right">{r.pct_of_total}%</TableCell>
              </TableRow>
            ))}
            {(dash.top_items || []).length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-4">—</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </WastageSection>

      {/* Date-wise Summary — collapsed by default */}
      {(dash.by_day || []).length > 0 && (
        <WastageSection open={openSections.daily} onToggle={() => toggleSection("daily")} testId="wc-section-daily"  title="Date-wise Summary" hint="worst day first">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Entries</TableHead>
                <TableHead className="text-right">Wastage ₹</TableHead>
                <TableHead className="text-right">Pot. Sales ₹</TableHead>
                <TableHead className="text-right">Wastage %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dash.by_day.slice(0, 15).map((d) => (
                <TableRow key={d.date} data-testid={`wc-day-${d.date}`}>
                  <TableCell className="font-medium">{d.date}</TableCell>
                  <TableCell className="text-right">{d.entries}</TableCell>
                  <TableCell className="text-right font-semibold text-purple-700">{fmt(d.food_cost_wastage)}</TableCell>
                  <TableCell className="text-right font-medium text-amber-700">{fmt(d.potential_sales_lost)}</TableCell>
                  <TableCell className="text-right">{d.wastage_pct}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WastageSection>
      )}

      {/* Detailed Wastage Report — collapsed by default. iter183: the
          collapse control lives inside DetailReportCard so the card
          keeps its own toolbar (totals · Excel · PDF · Show Filters). */}
      <DetailReportCard
        session={session}
        center={dash.center || center}
        fromDate={fromDate}
        toDate={toDate}
        reasonsPool={(dash.by_reason || []).map(r => r.reason)}
        sourcesPool={(dash.by_source || []).map(r => r.source)}
        drillReason={drillReason}
        onClearDrill={() => setDrillReason(null)}
        defaultExpanded={openSections.detail}
      />
    </div>
  );
}


function DailyTrendSVG({ series }) {
  const W = 900, H = 140, PAD = 20;
  const values = series.map((p) => p.food_cost_wastage);
  const max = Math.max(1, ...values);
  const pts = series.map((p, i) => {
    const x = PAD + (i * (W - 2 * PAD)) / Math.max(1, series.length - 1);
    const y = H - PAD - ((p.food_cost_wastage / max) * (H - 2 * PAD));
    return { x, y, ...p };
  });
  const poly = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const area = pts.length
    ? `M${pts[0].x.toFixed(1)},${(H - PAD).toFixed(1)} L` +
        pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ") +
        ` L${pts[pts.length - 1].x.toFixed(1)},${(H - PAD).toFixed(1)} Z`
    : "";
  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32">
        <path d={area} fill="rgba(147, 51, 234, 0.12)" />
        <polyline points={poly} fill="none" stroke="rgb(147, 51, 234)" strokeWidth="1.5" />
        {pts.map((p) => (
          <circle key={p.date} cx={p.x} cy={p.y} r="2.5" fill="rgb(147, 51, 234)" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        <span>{series[0]?.date}</span>
        <span>Peak ₹{Number(max).toLocaleString("en-IN")}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}


function AllCentresView({ data, isSA, onEditBenchmark }) {
  const rows = data?.rows || [];
  const t = data?.totals || {};
  const fmt = (v) => v == null ? "—"
    : `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total Food Cost" value={fmt(t.food_cost)} accent="slate" />
        <KPI label="Total Sales" value={fmt(t.period_sales)} accent="slate" />
        <KPI label="Total Actual Wastage ₹" value={fmt(t.actual_wastage)} accent="purple" />
        <KPI label="Total Excess Wastage ₹" value={fmt(t.excess_wastage)} accent="red" />
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">All Centres Comparison</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Centre</TableHead>
                  <TableHead className="text-right">Food Cost</TableHead>
                  <TableHead className="text-right">Sales</TableHead>
                  <TableHead className="text-right">Actual Wastage</TableHead>
                  <TableHead className="text-right">% Food Cost</TableHead>
                  <TableHead className="text-right">% Sales</TableHead>
                  <TableHead className="text-right">Benchmark %</TableHead>
                  <TableHead className="text-right">Allowed</TableHead>
                  <TableHead className="text-right">Excess</TableHead>
                  <TableHead>Status</TableHead>
                  {isSA && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={isSA ? 11 : 10}
                    className="text-center text-muted-foreground py-4">No centres.</TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.center} data-testid={`wc-all-row-${r.center}`}>
                    <TableCell className="font-medium">{r.center}</TableCell>
                    <TableCell className="text-right">{fmt(r.food_cost)}</TableCell>
                    <TableCell className="text-right">{fmt(r.period_sales)}</TableCell>
                    <TableCell className="text-right font-semibold text-purple-700">{fmt(r.actual_wastage)}</TableCell>
                    <TableCell className="text-right">{Number(r.wastage_pct_food_cost || 0).toFixed(2)}%</TableCell>
                    <TableCell className="text-right">{Number(r.wastage_pct_sales || 0).toFixed(4)}%</TableCell>
                    <TableCell className="text-right">
                      {r.wastage_benchmark_pct == null ? "—" : `${r.wastage_benchmark_pct}%`}
                    </TableCell>
                    <TableCell className="text-right">{r.allowed_wastage == null ? "—" : fmt(r.allowed_wastage)}</TableCell>
                    <TableCell className="text-right">{r.excess_wastage == null ? "—" : fmt(r.excess_wastage)}</TableCell>
                    <TableCell>
                      <Badge variant="outline"
                        className={`text-[10px] px-1.5 py-0 ${STATUS_STYLE[r.status] || ""}`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </Badge>
                    </TableCell>
                    {isSA && (
                      <TableCell>
                        <Button variant="ghost" size="sm"
                          onClick={() => onEditBenchmark(r.center)}
                          data-testid={`wc-set-benchmark-${r.center}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function BenchmarkModal({ center, session, initial, onClose, onSaved }) {
  const [bp, setBp] = useState(initial?.wastage_benchmark_pct ?? "");
  const [ap, setAp] = useState(initial?.wastage_amber_threshold_pct ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const bpN = bp === "" ? null : Number(bp);
    const apN = ap === "" ? null : Number(ap);
    if (bpN != null && (bpN < 0 || bpN > 100)) {
      toast.error("Benchmark % must be 0-100"); return;
    }
    if (apN != null && (apN < 0 || apN > 100)) {
      toast.error("Amber % must be 0-100"); return;
    }
    if (bpN != null && apN != null && apN < bpN) {
      toast.error("Amber threshold must be >= benchmark"); return;
    }
    setBusy(true);
    try {
      await api.post("/prepared-food-wastage/benchmark/save", {
        token: session.token, center,
        wastage_benchmark_pct: bpN,
        wastage_amber_threshold_pct: apN,
      });
      toast.success(`Benchmark saved for ${center}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-3"
        data-testid="wc-benchmark-modal">
        <h3 className="text-lg font-semibold">Wastage Benchmark — {center}</h3>
        <p className="text-xs text-muted-foreground">
          Benchmark is applied to <strong>Wastage % of Food Cost</strong>. Leave blank to clear.
        </p>
        <div className="space-y-1">
          <Label>Approved Benchmark %</Label>
          <Input type="number" min="0" max="100" step="0.1"
            value={bp} onChange={(e) => setBp(e.target.value)}
            placeholder="e.g. 2"
            data-testid="wc-benchmark-pct" />
        </div>
        <div className="space-y-1">
          <Label>Amber Threshold % (RED starts above this)</Label>
          <Input type="number" min="0" max="100" step="0.1"
            value={ap} onChange={(e) => setAp(e.target.value)}
            placeholder="e.g. 2.5"
            data-testid="wc-amber-pct" />
          <p className="text-[11px] text-muted-foreground">
            AMBER: above benchmark but ≤ amber. RED: above amber. Leave blank to make anything above benchmark RED.
          </p>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="wc-benchmark-save">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// iter182 — Detailed Wastage Report (filter · sort · export)
// ══════════════════════════════════════════════════════════════════════
function DetailReportCard({ session, center, fromDate, toDate,
                                 reasonsPool, sourcesPool,
                                 drillReason, onClearDrill,
                                 defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ entries: 0, wastage_cost: 0, potential_sales_lost: 0 });
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  // filters
  const [fSources, setFSources] = useState([]);
  const [fReasons, setFReasons] = useState([]);
  const [fItemQ, setFItemQ] = useState("");
  const [fGeneral, setFGeneral] = useState("");
  const [fMin, setFMin] = useState("");
  const [fMax, setFMax] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // Apply drill from clicking a Reason row upstream.
  useEffect(() => {
    if (drillReason) {
      setFReasons([drillReason]);
      setShowFilters(true);
      setExpanded(true);   // iter183 auto-open when drilled
    }
  }, [drillReason]);

  const buildBody = useCallback(() => {
    const filters = {};
    if (fSources.length) filters.sources = fSources;
    if (fReasons.length) filters.reasons = fReasons;
    if (fItemQ.trim()) filters.item_query = fItemQ.trim();
    if (fGeneral.trim()) filters.general_search = fGeneral.trim();
    if (fMin !== "" && !Number.isNaN(Number(fMin))) filters.wastage_min = Number(fMin);
    if (fMax !== "" && !Number.isNaN(Number(fMax))) filters.wastage_max = Number(fMax);
    return {
      token: session.token, center, from_date: fromDate, to_date: toDate,
      filters: Object.keys(filters).length ? filters : undefined,
      sort_by: sortBy, sort_dir: sortDir,
    };
  }, [session.token, center, fromDate, toDate,
        fSources, fReasons, fItemQ, fGeneral, fMin, fMax, sortBy, sortDir]);

  const load = useCallback(async () => {
    if (!expanded) return;                  // iter183 skip fetch while collapsed
    if (!center || center === "ALL") return;
    setLoading(true);
    try {
      const r = await api.post("/prepared-food-wastage/detail-report", buildBody());
      setRows(r.data?.rows || []);
      setTotals(r.data?.totals || { entries: 0, wastage_cost: 0, potential_sales_lost: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load detail report");
      setRows([]);
    } finally { setLoading(false); }
  }, [buildBody, center, expanded]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("desc"); }
  };
  const renderSortIcon = (col) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 inline opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 inline text-purple-600" />
                                    : <ArrowDown className="w-3 h-3 inline text-purple-600" />;
  };

  const toggleInArr = (arr, setter, value) => {
    setter(arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value]);
  };

  const clearFilters = () => {
    setFSources([]); setFReasons([]); setFItemQ(""); setFGeneral("");
    setFMin(""); setFMax(""); onClearDrill?.();
  };

  const exportFile = async (kind) => {
    try {
      const r = await api.post(`/prepared-food-wastage/detail-report/${kind}`,
        buildBody(), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      const ext = kind === "excel" ? "xlsx" : "pdf";
      a.download = `wastage_detail_${center}_${fromDate}_${toDate}.${ext}`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind.toUpperCase()} export failed`);
    }
  };

  const activeFilterCount = fSources.length + fReasons.length
    + (fItemQ ? 1 : 0) + (fGeneral ? 1 : 0)
    + (fMin !== "" ? 1 : 0) + (fMax !== "" ? 1 : 0);

  return (
    <Card data-testid="wc-detail-report">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3 flex-wrap">
          <button type="button"
            onClick={() => setExpanded(x => !x)}
            className="flex items-center gap-2 group text-left"
            data-testid="wc-section-toggle-detail"
            aria-expanded={expanded}>
            {expanded ? <ChevronDown className="w-4 h-4 text-slate-500 group-hover:text-purple-600" />
                       : <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-purple-600" />}
            <CardTitle className="text-base flex items-center gap-2 group-hover:text-purple-700 transition-colors">
              <Filter className="w-4 h-4 text-purple-600" />
              Detailed Wastage Report
            </CardTitle>
          </button>
          {!expanded && (
            <span className="text-[11px] text-slate-400">
              Excel-like filters · search · sort · export
            </span>
          )}
          {expanded && activeFilterCount > 0 && (
            <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-200 text-[11px]">
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} active
            </Badge>
          )}
          {expanded && (
            <span className="text-[11px] text-slate-500">
              {totals.entries} entries · Wastage {fmtINR(totals.wastage_cost)} · Potential Sales Lost {fmtINR(totals.potential_sales_lost)}
            </span>
          )}
          {expanded && (
            <div className="ml-auto flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowFilters(x => !x)} data-testid="wc-detail-toggle-filters">
                {showFilters ? "Hide" : "Show"} Filters
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportFile("excel")} data-testid="wc-detail-excel">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="wc-detail-pdf">
                <FileText className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      {expanded && (
      <CardContent className="space-y-3">
        {showFilters && (
          <div className="bg-slate-50 border rounded-lg p-3 space-y-3" data-testid="wc-detail-filters">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px]">Source</Label>
                <div className="flex flex-wrap gap-1">
                  {["MENU", "RAW", "PREP"].map(s => (
                    <button key={s} type="button"
                      onClick={() => toggleInArr(fSources, setFSources, s)}
                      data-testid={`wc-f-src-${s}`}
                      className={`px-2 py-1 rounded text-[11px] border transition-colors ${
                        fSources.includes(s) ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Reason</Label>
                <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                  {(reasonsPool || []).map(r => (
                    <button key={r} type="button"
                      onClick={() => toggleInArr(fReasons, setFReasons, r)}
                      data-testid={`wc-f-reason-${r.replace(/[^a-zA-Z0-9]/g, "-")}`}
                      className={`px-2 py-1 rounded text-[11px] border transition-colors ${
                        fReasons.includes(r) ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}>
                      {r}
                    </button>
                  ))}
                  {(reasonsPool || []).length === 0 && (
                    <span className="text-[11px] text-slate-400">no reasons in period</span>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Item contains</Label>
                <Input value={fItemQ} onChange={(e) => setFItemQ(e.target.value)}
                  placeholder="e.g. matki" className="h-8 text-xs"
                  data-testid="wc-f-item-q" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Search anywhere</Label>
                <Input value={fGeneral} onChange={(e) => setFGeneral(e.target.value)}
                  placeholder="item / reason / source / date / user"
                  className="h-8 text-xs" data-testid="wc-f-general" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Wastage ₹ min</Label>
                <Input type="number" value={fMin} onChange={(e) => setFMin(e.target.value)}
                  className="h-8 text-xs" data-testid="wc-f-min" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px]">Wastage ₹ max</Label>
                <Input type="number" value={fMax} onChange={(e) => setFMax(e.target.value)}
                  className="h-8 text-xs" data-testid="wc-f-max" />
              </div>
              <div className="lg:col-span-2 flex items-end justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="wc-f-clear">
                  <X className="w-3.5 h-3.5 mr-1" /> Clear all
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("date")} data-testid="wc-sort-date">
                  Date {renderSortIcon("date")}
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("item")} data-testid="wc-sort-item">
                  Item {renderSortIcon("item")}
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("source")} data-testid="wc-sort-source">
                  Source {renderSortIcon("source")}
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("reason")} data-testid="wc-sort-reason">
                  Reason {renderSortIcon("reason")}
                </TableHead>
                <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort("qty")} data-testid="wc-sort-qty">
                  Qty {renderSortIcon("qty")}
                </TableHead>
                <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort("wastage")} data-testid="wc-sort-wastage">
                  Wastage ₹ {renderSortIcon("wastage")}
                </TableHead>
                <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort("potential")} data-testid="wc-sort-potential">
                  Pot. Sales ₹ {renderSortIcon("potential")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-6"><Loader2 className="w-4 h-4 animate-spin inline" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No wastage entries match the current filters.</TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={r.id || `${r.date}-${r.item}-${i}`} data-testid={`wc-detail-row-${i}`}>
                  <TableCell className="text-xs text-slate-500">{r.date}</TableCell>
                  <TableCell className="font-medium">{r.item}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${SOURCE_STYLE[r.source] || ""}`}>
                      {r.source}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{r.reason}</TableCell>
                  <TableCell className="text-right">{r.quantity} {r.unit}</TableCell>
                  <TableCell className="text-right font-semibold text-purple-700">{fmtINR(r.wastage_cost)}</TableCell>
                  <TableCell className="text-right font-medium text-amber-700">
                    {r.potential_sales_lost == null ? "—" : fmtINR(r.potential_sales_lost)}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length > 0 && (
                <TableRow className="bg-amber-50 font-semibold">
                  <TableCell colSpan={4}>TOTAL</TableCell>
                  <TableCell className="text-right">{totals.entries} rows</TableCell>
                  <TableCell className="text-right text-purple-700">{fmtINR(totals.wastage_cost)}</TableCell>
                  <TableCell className="text-right text-amber-700">{fmtINR(totals.potential_sales_lost)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <p className="text-[11px] text-slate-400 italic">
          Potential Sales Lost = wasted MENU qty × current centre POS price. RAW / PREP have no direct selling price. This is an analytical figure only — never added to accounting, salary or P&amp;L.
        </p>
      </CardContent>
      )}
    </Card>
  );
}
